import { capturePreviewSnapshot } from '@/utils/capturePreview';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Shirt, Box } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCart } from '@/context/CartContext';
import FabricStep from '@/components/FabricStep';
import OptionsStep, { PIPING_OPTIONS } from '@/components/OptionsStep';
import BolsterCushion3DPreview from '@/components/BolsterCushion3DPreview';
import FoamInlineSection from '@/components/FoamInlineSection';
import { FOAM_DENSITIES } from '@/components/cushionConstants';
import { CustomizerLayout, ProductTypeStep, buildProductImages, PreviewGallery, ReviewStep } from '@/components/customizer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSHION_ID = 'bolster-cushion';

const GALLERY_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1654801816121-2beac3d9af53?w=800&q=80', alt: 'Decorative bolster cushion' },
  { src: 'https://images.unsplash.com/photo-1696774276390-6ce82111140f?w=800&q=80', alt: 'Bolster cushions on sofa' },
  { src: 'https://images.unsplash.com/photo-1708936120452-6dffc2a096e1?w=800&q=80', alt: 'Close-up of bolster cushion' },
];

const PRODUCT_TYPE_OPTIONS = [
  { value: 'cover_and_foam', label: 'Cover & Foam Insert', description: 'Complete cushion with cover and foam', icon: Package },
  { value: 'cover_only', label: 'Cover Only', description: 'Just the fabric cover, no foam', icon: Shirt },
  { value: 'foam_only', label: 'Foam Insert Only', description: 'Just the foam insert, no cover', icon: Box },
];

const BolsterCushionPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [productType, setProductType] = useState('cover_and_foam');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const stepContentRef = useRef(null);

  const [cushion, setCushion] = useState(null);

  const [dimensionsCm, setDimensionsCm] = useState({ diameter: 0, length: 0 });
  const [inputValues, setInputValues] = useState({ diameter: '', length: '' });
  const [unit, setUnit] = useState('cm');

  const [selectedFabric, setSelectedFabric] = useState(null);
  const [selectedFoam, setSelectedFoam] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [priceInfo, setPriceInfo] = useState(null);

  const [selectedFabricRange, setSelectedFabricRange] = useState(null);
  const [selectedFabricColor, setSelectedFabricColor] = useState(null);
  const [fabricZoom, setFabricZoom] = useState(3);
  const [fabricRanges, setFabricRanges] = useState([]);
  const [pipingOption, setPipingOption] = useState('none');
  const [pipingFabricRange, setPipingFabricRange] = useState(null);
  const [pipingFabricColor, setPipingFabricColor] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const steps = useMemo(() => {
    const base = [
      { name: 'Product', description: 'Select type' },
      { name: 'Dimensions', description: 'Enter measurements' },
    ];
    if (productType === 'cover_and_foam') return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Foam', description: 'Select foam' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
    if (productType === 'cover_only') return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
    return [...base, { name: 'Foam', description: 'Select foam' }, { name: 'Review', description: 'Confirm order' }];
  }, [productType]);

  const currentStepName = steps[currentStep]?.name || 'Product';
  const totalSteps = steps.length;

  const cmToUnit = useCallback((cm, u) => u === 'mm' ? cm * 10 : cm, []);
  const unitToCm = useCallback((value, u) => u === 'mm' ? value / 10 : value, []);

  const getDisplayValue = useCallback((field) => {
    if (inputValues[field] !== undefined && inputValues[field] !== '') return inputValues[field];
    const value = cmToUnit(dimensionsCm[field], unit);
    return value === 0 ? '' : value;
  }, [dimensionsCm, unit, cmToUnit, inputValues]);

  const handleDimensionInput = (field, rawValue) => {
    const filteredValue = rawValue.replace(/[^0-9.]/g, '');
    const parts = filteredValue.split('.');
    const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : filteredValue;
    setInputValues(prev => ({ ...prev, [field]: cleanValue }));
    if (cleanValue === '' || cleanValue === null) {
      setDimensionsCm(prev => ({ ...prev, [field]: 0 }));
    } else {
      const numValue = parseFloat(cleanValue);
      if (!isNaN(numValue)) setDimensionsCm(prev => ({ ...prev, [field]: unitToCm(numValue, unit) }));
    }
  };

  const handleDimensionBlur = (field) => {
    setInputValues(prev => ({ ...prev, [field]: '' }));
    setActiveField(null);
  };

  const handleDimensionFocus = (field) => {
    const displayVal = cmToUnit(dimensionsCm[field], unit);
    setInputValues(prev => ({ ...prev, [field]: displayVal === 0 ? '' : String(displayVal) }));
    setActiveField(field);
  };

  const getLimitInUnit = useCallback((field, limitType) => {
    const limits = { diameter: { min: 10, max: 60 }, length: { min: 20, max: 150 } };
    return cmToUnit(limits[field]?.[limitType] || 0, unit);
  }, [unit, cmToUnit]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [cushionRes, fabricRangesRes] = await Promise.all([
          axios.get(`${API}/cushion-types/${CUSHION_ID}`),
          axios.get(`${API}/fabric-ranges`)
        ]);
        setCushion(cushionRes.data);
        setFabricRanges(fabricRangesRes.data);
      } catch (error) {
        toast.error('Failed to load product data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const calculatePrice = useCallback(async () => {
    if (!cushion) return;
    const hasFabricSelection = (selectedFabricRange && selectedFabricColor) || selectedFabric;
    if (productType === 'cover_and_foam' && (!hasFabricSelection || !selectedFoam)) return;
    if (productType === 'cover_only' && !hasFabricSelection) return;
    if (productType === 'foam_only' && !selectedFoam) return;
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        cushion_type_id: CUSHION_ID,
        dimensions: { length: dimensionsCm.length, width: dimensionsCm.diameter, thickness: dimensionsCm.diameter },
        product_type: productType,
        fabric_id: selectedFabricRange ? null : (productType !== 'foam_only' ? selectedFabric : null),
        fabric_range_id: productType !== 'foam_only' ? selectedFabricRange : null,
        fabric_color_id: productType !== 'foam_only' ? selectedFabricColor : null,
        foam_type_id: productType !== 'cover_only' ? selectedFoam : null,
        quantity
      });
      setPriceInfo(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  }, [cushion, dimensionsCm, productType, selectedFabric, selectedFabricRange, selectedFabricColor, selectedFoam, quantity]);

  useEffect(() => {
    const debounce = setTimeout(calculatePrice, 300);
    return () => clearTimeout(debounce);
  }, [calculatePrice]);

  const handleAddToCart = async () => {
    if (!priceInfo) return;
    setAddingToCart(true);
    const preview_image = await capturePreviewSnapshot();
    try {
      const success = await addToCart({
        cushion_type_id: CUSHION_ID,
        dimensions: { length: dimensionsCm.length, width: dimensionsCm.diameter, thickness: dimensionsCm.diameter },
        product_type: productType,
        fabric_id: selectedFabricRange ? null : (productType !== 'foam_only' ? selectedFabric : null),
        fabric_range_id: productType !== 'foam_only' ? selectedFabricRange : null,
        fabric_color_id: productType !== 'foam_only' ? selectedFabricColor : null,
        foam_type_id: productType !== 'cover_only' ? selectedFoam : null,
        quantity,
        zip_placement: 'length',
        piping_option: pipingOption !== 'none' ? pipingOption : null,
        piping_fabric_range_id: pipingOption === 'different' ? pipingFabricRange : null,
        piping_fabric_color_id: pipingOption === 'different' ? pipingFabricColor : null,
        preview_image,
      });
      if (success) {
        toast.success('Added to cart!', {
          description: `Bolster Cushion x${quantity}`,
          action: { label: 'View Cart', onClick: () => navigate('/cart') }
        });
      } else {
        toast.error('Failed to add to cart');
      }
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      setTimeout(() => {
        const nextName = steps[next]?.name;
        if (nextName === 'Fabric' && stepContentRef.current && window.innerWidth < 1024) {
          const rect = stepContentRef.current.getBoundingClientRect();
          window.scrollTo({ top: window.scrollY + rect.top + 200, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    }
  };

  const canProceed = () => {
    switch (currentStepName) {
      case 'Dimensions': return dimensionsCm.length > 0 && dimensionsCm.diameter > 0;
      case 'Foam': return !!selectedFoam;
      case 'Product': return productType !== null;
      case 'Fabric': return (selectedFabricRange !== null && selectedFabricColor !== null) || selectedFabric !== null;
      default: return true;
    }
  };

  const selectedFoamObj = selectedFoam ? FOAM_DENSITIES[selectedFoam] : null;
  const [searchFabricData, setSearchFabricData] = useState(null);
  const selectedFabricRangeObj = useMemo(() => fabricRanges.find(r => r.id === selectedFabricRange), [fabricRanges, selectedFabricRange]);
  const selectedFabricColorObj = useMemo(() => selectedFabricRangeObj?.colors?.find(c => c.id === selectedFabricColor), [selectedFabricRangeObj, selectedFabricColor]);
  const displayFabricName = useMemo(() => { if (searchFabricData) return `${searchFabricData.rangeName} - ${searchFabricData.colorName}`; return (selectedFabricRangeObj && selectedFabricColorObj) ? `${selectedFabricRangeObj.name} - ${selectedFabricColorObj.name}` : '-'; }, [selectedFabricRangeObj, selectedFabricColorObj, searchFabricData]);
  const fabricColorForPreview = selectedFabricColorObj?.hex_color || null;
  const fabricImageUrl = useMemo(() => {
    if (searchFabricData?.imageUrl) return searchFabricData.imageUrl;
    const url = selectedFabricColorObj?.image_url;
    if (url?.includes('warwick.com.au') || url?.startsWith('/api/fabric-images')) return url;
    return null;
  }, [selectedFabricColorObj, searchFabricData]);
  const pipingCost = useMemo(() => PIPING_OPTIONS.find(o => o.value === pipingOption)?.price || 0, [pipingOption]);
  const pipingHexColor = useMemo(() => { if (pipingOption === 'different' && pipingFabricRange && pipingFabricColor) { const pRange = fabricRanges.find(r => r.id === pipingFabricRange); return pRange?.colors?.find(c => c.id === pipingFabricColor)?.hex_color || '#f5f0eb'; } return null; }, [pipingOption, pipingFabricRange, pipingFabricColor, fabricRanges]);
  const pipingImageUrlComputed = useMemo(() => { if (pipingOption === 'same') return fabricImageUrl; if (pipingOption === 'different' && pipingFabricRange && pipingFabricColor) { const pRange = fabricRanges.find(r => r.id === pipingFabricRange); const pColor = pRange?.colors?.find(c => c.id === pipingFabricColor); const u = pColor?.image_url; return (u?.includes('warwick.com.au') || u?.startsWith('/api/fabric-images')) ? u : null; } return null; }, [pipingOption, fabricImageUrl, pipingFabricRange, pipingFabricColor, fabricRanges]);

  const configRows = useMemo(() => {
    const rows = [
      { label: 'Product Type', value: PRODUCT_TYPE_OPTIONS.find(p => p.value === productType)?.label || '-' },
      { label: 'Length', value: dimensionsCm.length > 0 ? `${unit === 'mm' ? dimensionsCm.length * 10 : dimensionsCm.length} ${unit}` : '-' },
      { label: 'Diameter', value: dimensionsCm.diameter > 0 ? `${unit === 'mm' ? dimensionsCm.diameter * 10 : dimensionsCm.diameter} ${unit}` : '-' },
    ];
    if (productType !== 'foam_only') {
      rows.push({
        label: 'Fabric',
        value: (fabricColorForPreview || searchFabricData)
          ? <span className="flex items-center justify-end gap-2"><span className="w-4 h-4 rounded-full border" style={{ backgroundColor: fabricColorForPreview }}></span>{displayFabricName}</span>
          : '-'
      });
    }
    if (productType !== 'cover_only') {
      rows.push({ label: 'Foam', value: selectedFoamObj?.name || '-' });
    }
    if (pipingOption !== 'none') {
      const pLabel = PIPING_OPTIONS.find(o => o.value === pipingOption)?.label || '-';
      if (pipingOption === 'different') {
        const pRange = fabricRanges.find(r => r.id === pipingFabricRange);
        const pColor = pRange?.colors?.find(c => c.id === pipingFabricColor);
        rows.push({ label: 'Piping', value: pColor ? <span className="flex items-center justify-end gap-2"><span className="w-4 h-4 rounded-full border" style={{ backgroundColor: pColor.hex_color }}></span>{pRange.name} - {pColor.name}</span> : pLabel });
      } else {
        rows.push({ label: 'Piping', value: pLabel });
      }
    }
    return rows;
  }, [productType, dimensionsCm, unit, fabricColorForPreview, displayFabricName, searchFabricData, selectedFoamObj, pipingOption, fabricRanges, pipingFabricRange, pipingFabricColor]);

  const previewPanel = (
    <PreviewGallery
      galleryImages={GALLERY_IMAGES}
      galleryIndex={galleryIndex}
      setGalleryIndex={setGalleryIndex}
      configRows={configRows}
      priceInfo={priceInfo}
      pipingCost={pipingCost}
      showZipLabel={productType !== 'foam_only'}
    >
      <BolsterCushion3DPreview
        diameter={dimensionsCm.diameter > 0 ? dimensionsCm.diameter : 20}
        length={dimensionsCm.length > 0 ? dimensionsCm.length : 70}
        fabricColor={productType === 'foam_only' ? '#d4a843' : fabricColorForPreview}
        fabricImageUrl={productType === 'foam_only' ? '/foam-texture.png' : fabricImageUrl}
        fabricZoom={fabricZoom}
        showZip={productType !== 'foam_only'}
        rawDiameter={dimensionsCm.diameter}
        rawLength={dimensionsCm.length}
        unit={unit}
        activeField={activeField}
        pipingOption={pipingOption}
        pipingColor={pipingHexColor}
        pipingImageUrl={pipingImageUrlComputed}
      />
    </PreviewGallery>
  );

  const contentPanel = (
    <div ref={stepContentRef}>
      {currentStepName === 'Product' && (
        <ProductTypeStep options={PRODUCT_TYPE_OPTIONS} value={productType} onChange={setProductType} productName="Bolster Cushion" productDescription="Cylindrical bolster cushion for daybeds, sofas, and headrests. Pick your length, diameter, fabric, and piping — a classic finishing touch that adds support and style to any seating arrangement." productImages={buildProductImages(GALLERY_IMAGES)} />
      )}

      {currentStepName === 'Dimensions' && (
        <div data-testid="step-dimensions">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <h2 className="text-lg font-semibold text-[#1A1A1A]">Enter Dimensions</h2>
            <Tabs value={unit} onValueChange={setUnit} className="w-auto">
              <TabsList className="h-7 sm:h-9 bg-sand p-0.5">
                <TabsTrigger value="cm" className="text-xs sm:text-sm px-2 sm:px-3 h-6 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded" data-testid="unit-cm">cm</TabsTrigger>
                <TabsTrigger value="mm" className="text-xs sm:text-sm px-2 sm:px-3 h-6 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded" data-testid="unit-mm">mm</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <p className="hidden sm:block text-gray-600 mb-6 text-sm">Enter the diameter and length of your bolster cushion.</p>

          <div className="space-y-4 sm:space-y-6">
            <div className="space-y-1 sm:space-y-2">
              <div className="flex items-center gap-1 sm:gap-2 relative">
                <Label className="text-xs sm:text-base font-semibold flex items-center gap-1 sm:gap-2">
                  <span className="w-5 h-5 sm:w-8 sm:h-8 rounded-full bg-bush/10 flex items-center justify-center text-bush text-[10px] sm:text-sm font-bold">L</span>
                  <span>Length</span>
                </Label>
              </div>
              <div className="relative">
                <Input type="text" inputMode="decimal" placeholder="0" value={getDisplayValue('length')} onChange={(e) => handleDimensionInput('length', e.target.value)} onBlur={() => handleDimensionBlur('length')} onFocus={() => handleDimensionFocus('length')} className="h-10 sm:h-14 text-base sm:text-xl font-semibold text-center bg-sand border-2 border-transparent focus:border-bush rounded-lg sm:rounded-xl pr-8 sm:pr-12 placeholder:text-gray-300" data-testid="length-input" />
                <span className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-xs sm:text-base">{unit}</span>
              </div>
              <p className="hidden sm:block text-xs text-gray-500 mt-[5px]">The length of the bolster cushion</p>
            </div>

            <div className="space-y-1 sm:space-y-2">
              <Label className="text-xs sm:text-base font-semibold flex items-center gap-1 sm:gap-2">
                <span className="w-5 h-5 sm:w-8 sm:h-8 rounded-full bg-bush/10 flex items-center justify-center text-bush text-[10px] sm:text-sm font-bold">D</span>
                <span>Diameter</span>
              </Label>
              <div className="flex flex-wrap gap-1.5 sm:gap-3">
                {[15, 20].map((d) => {
                  const displayD = unit === 'mm' ? d * 10 : d;
                  const isSelected = dimensionsCm.diameter === d;
                  return (
                    <button key={d} onClick={() => { setDimensionsCm(prev => ({ ...prev, diameter: d })); setInputValues(prev => ({ ...prev, diameter: '' })); }} className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border-2 text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer ${isSelected ? 'bg-teal-600 border-teal-600 text-white shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'}`} data-testid={`diameter-${d}`}>
                      {displayD} {unit}
                    </button>
                  );
                })}
              </div>
              <p className="hidden sm:block text-xs text-gray-500 mt-[5px]">The diameter of the circular end</p>
            </div>
          </div>
        </div>
      )}

      {currentStepName === 'Foam' && (
        <div data-testid="step-foam">
          <FoamInlineSection thicknessCm={null} selectedFoam={selectedFoam} onFoamSelect={setSelectedFoam} productType={productType} showAllFoams={true} />
        </div>
      )}

      {currentStepName === 'Fabric' && (
        <FabricStep selectedRange={selectedFabricRange} selectedColor={selectedFabricColor} onRangeSelect={setSelectedFabricRange} onColorSelect={setSelectedFabricColor} cushionCategory={cushion.category} fabricZoom={fabricZoom} onFabricZoomChange={setFabricZoom} onSearchFabricSelect={setSearchFabricData} />
      )}

      {currentStepName === 'Options' && (
        <OptionsStep pipingOption={pipingOption} onPipingChange={setPipingOption} pipingFabricRange={pipingFabricRange} pipingFabricColor={pipingFabricColor} onPipingRangeSelect={setPipingFabricRange} onPipingColorSelect={setPipingFabricColor} cushionCategory={cushion.category} mainFabricRange={selectedFabricRange} mainFabricColor={selectedFabricColor} />
      )}

      {currentStepName === 'Review' && (
        <ReviewStep
          cushionName={cushion.name}
          productType={productType}
          productTypeLabel={PRODUCT_TYPE_OPTIONS.find(p => p.value === productType)?.label}
          dimensionsSummary={`${dimensionsCm.diameter} cm dia x ${dimensionsCm.length} cm long`}
          fabricColorForPreview={fabricColorForPreview}
          displayFabricName={displayFabricName}
          selectedFoamName={selectedFoamObj?.name}
          pipingOption={pipingOption}
          pipingCost={pipingCost}
          pipingFabricName={pipingOption === 'different' ? (() => { const r = fabricRanges.find(r => r.id === pipingFabricRange); const c = r?.colors?.find(c => c.id === pipingFabricColor); return r && c ? `${r.name} - ${c.name}` : null; })() : null}
          quantity={quantity}
          setQuantity={setQuantity}
          priceInfo={priceInfo}
        />
      )}
    </div>
  );

  return (
    <CustomizerLayout
      cushion={cushion}
      loading={loading}
      steps={steps}
      currentStepIndex={currentStep}
      currentStepName={currentStepName}
      onStepClick={setCurrentStep}
      isFirstStep={currentStep === 0}
      isReviewStep={currentStepName === 'Review'}
      canProceed={canProceed()}
      onBack={prevStep}
      onNext={nextStep}
      onAddToCart={handleAddToCart}
      addingToCart={addingToCart}
      priceInfo={priceInfo}
      pipingCost={pipingCost}
      previewPanel={previewPanel}
      contentPanel={contentPanel}
      pageTestId="bolster-cushion-page"
    />
  );
};

export default BolsterCushionPage;
